import * as React from "react";
import {
    BrowserRouter as Router,
    Routes,
    Route,
    Navigate,
} from "react-router-dom";

import Providers from "@/context/Providers";
import { GlobalAdminProtectedRoute } from "@/components/auth/GlobalAdminProtectedRoute";
import { useHttpsRedirect } from "@/hooks/useHttpsRedirect";
import LoginPage from "@/pages/LoginPage";
import AuthCallbackPage from "@/pages/AuthCallbackPage";
import GlobalAdminLayout from "@/components/layout/GlobalAdminLayout";
import ApiKeysPage from "@/pages/ApiKeysPage";
import ServerConfigsPage from "@/pages/ServerConfigsPage";
import CurrencyRegionsPage from "@/pages/pricing/CurrencyRegionsPage";
import CloudServersPage from "@/pages/pricing/CloudServersPage";
import LicenceFeesPage from "@/pages/pricing/LicenceFeesPage";
import AdvisorFeesPage from "@/pages/pricing/AdvisorFeesPage";
import EnterprisePlansPage from "@/pages/EnterprisePlansPage";
import OnPremAocPage from "@/pages/OnPremAocPage";
import AocUpdatesPage from "@/pages/AocUpdatesPage";
import RegistryKeysPage from "@/pages/RegistryKeysPage";
import SupplyChainPage from "@/pages/SupplyChainPage";
import SoftwarePage from "@/pages/SoftwarePage";
import EnterpriseClientsPage from "@/pages/EnterpriseClientsPage";
import BillingPage from "@/pages/BillingPage";
import CustomersPage from "@/pages/CustomersPage";
import ServersPage from "@/pages/ServersPage";
import CustomerSupportPage from "@/pages/CustomerSupportPage";
import OutboxPage from "@/pages/OutboxPage";
import MessagesPage from "@/pages/MessagesPage";
import InvoicesPage from "@/pages/InvoicesPage";
import PayoutsPage from "@/pages/PayoutsPage";
import { useGlobalAdminStatus } from "@/hooks/useGlobalAdminStatus";
import FreeTrialsPage from '@/pages/FreeTrialsPage';
import UserManagementPage from "@/pages/UserManagementPage";
import AdvisorReportsPage from "@/pages/AdvisorReportsPage";
import AdvisorsPage from "@/pages/AdvisorsPage";
import SignInProvidersPage from "@/pages/SignInProvidersPage";

function HomeRedirect() {
    const { role, isLoading } = useGlobalAdminStatus();
    if (isLoading) return null;
    return <Navigate to={role === 'support' ? '/customers' : '/api-keys'} replace />;
}

function AdminOnly({ children }: { children: React.ReactElement }) {
    const { role, isLoading } = useGlobalAdminStatus();
    if (isLoading) return null;
    if (role === 'support') return <Navigate to="/customers" replace />;
    return children;
}

function App() {
    useHttpsRedirect();

    return (
        <Providers>
            <Router>
                <Routes>
                    {/* Public routes */}
                    <Route path="/login" element={<LoginPage />} />
                    <Route path="/auth/callback" element={<AuthCallbackPage />} />

                    {/* Global Admin protected routes */}
                    <Route
                        path="/"
                        element={
                            <GlobalAdminProtectedRoute>
                                <GlobalAdminLayout />
                            </GlobalAdminProtectedRoute>
                        }
                    >
                        <Route index element={<HomeRedirect />} />
                        <Route path="api-keys" element={<AdminOnly><ApiKeysPage /></AdminOnly>} />
                        <Route path="server-configs" element={<AdminOnly><ServerConfigsPage /></AdminOnly>} />
                        <Route path="pricing/currency-regions" element={<AdminOnly><CurrencyRegionsPage /></AdminOnly>} />
                        <Route path="pricing/cloud-servers" element={<AdminOnly><CloudServersPage /></AdminOnly>} />
                        <Route path="pricing/licence-fees" element={<AdminOnly><LicenceFeesPage /></AdminOnly>} />
                        <Route path="pricing/advisor-fees" element={<AdminOnly><AdvisorFeesPage /></AdminOnly>} />
                        <Route path="enterprise-plans" element={<EnterprisePlansPage />} />
                        <Route path="on-prem-aoc" element={<OnPremAocPage />} />
                        <Route path="downloads" element={<SoftwarePage />} />
                        <Route path="updates" element={<AdminOnly><AocUpdatesPage /></AdminOnly>} />
                        <Route path="supply-chain" element={<SupplyChainPage />} />
                        <Route path="registry-keys" element={<AdminOnly><RegistryKeysPage /></AdminOnly>} />
                        <Route path="billing" element={<AdminOnly><BillingPage /></AdminOnly>} />
                        <Route path="free-trials" element={<AdminOnly><FreeTrialsPage /></AdminOnly>} />
                        <Route path="enterprise-clients" element={<EnterpriseClientsPage />} />
                        <Route path="customers" element={<CustomersPage />} />
                        <Route path="customers/:orgId" element={<CustomerSupportPage />} />
                        <Route path="servers" element={<ServersPage />} />
                        <Route path="outbox" element={<OutboxPage />} />
                        <Route path="messages" element={<MessagesPage />} />
                        <Route path="invoices" element={<InvoicesPage />} />
                        <Route path="payouts" element={<PayoutsPage />} />
                        <Route path="users" element={<AdminOnly><UserManagementPage /></AdminOnly>} />
                        <Route path="advisors" element={<AdvisorsPage />} />
                        <Route path="sign-in-providers" element={<AdminOnly><SignInProvidersPage /></AdminOnly>} />
                        <Route path="advisor-reports" element={<AdvisorReportsPage />} />
                    </Route>

                    {/* Catch all */}
                    <Route path="*" element={<Navigate to="/api-keys" replace />} />
                </Routes>
            </Router>

        </Providers>
    );
}

export default App;
